import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getData, getDataById, addData, updateData, geteventType, getcountries, getseriesType,
  getReceiptId, getprogramManager, getconferenceEvent, getwebinarEvent, getinvitationCategory
} from '../../../services/event/invitationLetter/invitationLetterServices';
import { getData as getAbstractReceiptData } from '../../../services/event/abstractReceipt/abstractReceiptServices';
import { Form, Spinner as BsSpinner } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import Header from '../../../components/Layout/Header';
import Footer from '../../../components/Layout/Footer';
import Sidebar from '../../../components/Layout/Sidebar';
import Spinner from '../../../components/Spinner';
import Swal from 'sweetalert2';
const subDomain = localStorage.getItem('sub_domain') || '';

const NO_ABSTRACT_CATEGORIES = ['delegate', 'reviewer'];

const isNoAbstractCategory = (category) => NO_ABSTRACT_CATEGORIES.includes((category || '').toLowerCase());
const isKeynoteCategory = (category) => (category || '').toLowerCase() === 'keynote speaker';
const isSpeakerCategory = (category) => (category || '').toLowerCase() === 'speaker';

const getAbstractTypeOptions = (category, eventType) => {
  if (isKeynoteCategory(category)) return ['Keynote presentation'];
  if (isSpeakerCategory(category)) {
    return (eventType || '').toLowerCase() === 'webinar'
      ? ['Virtual oral presentation', 'Video presentation']
      : ['Oral presentation', 'Poster presentation'];
  }
  return [];
};

const emptyForm = {
  id: '',
  invitation_id: '',
  series: '',
  venue: '',
  event_type: '',
  invitation_category: '',
  abstract_type: '',
  country: '',
  speaker_name: '',
  email: '',
  organization: '',
  abstract_title: '',
  conference_committee: '',
  conf_shortname: '',
  expertise: '',
  coordinator_name: '',
  designation: '',
  config_email: '',
  event_title: '',
  event_date: '',
  sub_domain: subDomain,
};

const InvitationLetter = () => {
  const [view, setView] = useState('list');
  const [formMode, setFormMode] = useState('');

  const [dataList, setDataList] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(true); // gates only the initial list fetch now
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});
  const [activeSeriesList, setActiveSeriesList] = useState([]);

  // Cache the program manager record once, reuse everywhere instead of
  // re-fetching it in openForm() and handleSubmit().
  const [programManagerData, setProgramManagerData] = useState(null);

  // Tracks whether the background program-manager fetch has resolved, so
  // openForm('add') can wait for it if clicked too early instead of
  // misreporting "Program Manager Not Found".
  const [pmLoaded, setPmLoaded] = useState(false);
  const pmFetchPromiseRef = useRef(null);

  const [useExisting, setUseExisting] = useState(null);
  const [abstractReceiptList, setAbstractReceiptList] = useState([]);
  const [selectedAbstractId, setSelectedAbstractId] = useState('');

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [dropdownData, setDropdownData] = useState({
    eventType: [],
    countries: [],
    invitationType: []
  });

  const navigate = useNavigate();

  const fetchDropdownData = useCallback(async () => {
    try {
      const [event, country, invitationCategory] = await Promise.all([geteventType(), getcountries(), getinvitationCategory()]);
      setDropdownData(prev => ({ ...prev, eventType: event, countries: country, invitationType: invitationCategory }));
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const data = await getData({ sub_domain: subDomain });
      setDataList(data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, []);

  // Fetches once, stores full record in state AND fills the program-manager
  // fields on the form — both openForm() and handleSubmit() now read from
  // programManagerData instead of calling the API again.
  const fetchProgramManager = useCallback(async () => {
    try {
      const data = await getprogramManager({ sub_domain: subDomain });
      if (data && data.length > 0) {
        const pm = data[0];
        setProgramManagerData(pm);
        setForm(prev => ({
          ...prev,
          conference_committee: pm.conference_committee || '',
          conf_shortname: pm.conf_shortname || '',
          expertise: pm.expertise || '',
          coordinator_name: pm.coordinator_name || '',
          designation: pm.designation || '',
          config_email: pm.config_email || ''
        }));
      } else {
        setProgramManagerData(null);
      }
    } catch (error) {
      console.error('Failed to fetch program manager:', error);
      setProgramManagerData(null);
    } finally {
      setPmLoaded(true);
    }
  }, []);

  const fetchActiveSeries = useCallback(async () => {
    try {
      const seriesType = await getseriesType();
      return (seriesType || [])
        .filter((s) => s.is_active === 'active')
        .map((s) => s.series);
    } catch (error) {
      console.error('Failed to fetch active series:', error);
      return [];
    }
  }, []);

  useEffect(() => {
    const fetchDataAsync = async () => {
      setLoading(true);

      // Only the main list needs to block the spinner — this is the
      // content the user actually sees first.
      await fetchData();
      setLoading(false);

      // Everything else (dropdown lookups, program manager, active series)
      // is only needed once the Add/Edit form opens. Fetch it in the
      // background so it doesn't add to perceived page-load time.
      pmFetchPromiseRef.current = fetchProgramManager();

      Promise.all([
        fetchActiveSeries().then(setActiveSeriesList),
        fetchDropdownData(),
        pmFetchPromiseRef.current,
      ]).catch((error) => console.error('Background lookup fetch failed:', error));
    };

    fetchDataAsync();
  }, [fetchData, fetchDropdownData, fetchProgramManager, fetchActiveSeries]);

  const formatEventDate = (fromDate, toDate) => {
    if (!fromDate) return { date: "" };

    const from = new Date(fromDate);
    const year = from.getFullYear();

    const fromFormatted = from.toLocaleDateString("en-US", {
      month: "long",
      day: "2-digit"
    });

    if (!toDate || new Date(fromDate).toDateString() === new Date(toDate).toDateString()) {
      return { date: `${fromFormatted}, ${year}` };
    }

    const to = new Date(toDate);
    const toFormatted = to.toLocaleDateString("en-US", { day: "2-digit" });
    return { date: `${fromFormatted}-${toFormatted}, ${year}` };
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));

    if (name === 'invitation_category') {
      if (isNoAbstractCategory(value)) {
        setForm((prev) => ({ ...prev, abstract_type: 'N/A', abstract_title: '' }));
        setUseExisting(false);
        setSelectedAbstractId('');
      } else if (isKeynoteCategory(value)) {
        setForm((prev) => ({ ...prev, abstract_type: 'Keynote presentation' }));
      } else {
        setForm((prev) => ({ ...prev, abstract_type: '' }));
      }
    }

    if (name === 'event_type') {
      let eventData = [];
      try {
        if (value.toLowerCase() === 'conference') {
          eventData = await getconferenceEvent({ sub_domain: subDomain });
        } else if (value.toLowerCase() === 'webinar') {
          eventData = await getwebinarEvent({ sub_domain: subDomain });
        }

        if (eventData.length > 0) {
          const event = eventData[0];
          const eventDate = formatEventDate(event.from_date, event.to_date);

          setForm((prev) => ({
            ...prev,
            event_title: event.event_title,
            event_date: eventDate.date,
            series: event.series,
            venue: value.toLowerCase() === 'conference' ? event.venue || '' : 'NIL',
            abstract_type: isSpeakerCategory(prev.invitation_category) ? '' : prev.abstract_type,
          }));
        }
      } catch (error) {
        console.error('Error loading event details:', error);
      }
    }
  };

  const getCountryName = (code) => {
    const country = dropdownData.countries.find(c => c.code === code);
    return country ? country.name : code;
  };

  const combineAbstractTitles = (record) => {
    return [record.abstract_title1, record.abstract_title2, record.abstract_title3]
      .filter(Boolean)
      .join('; ');
  };

  const fetchAbstractReceiptList = useCallback(async () => {
    try {
      const data = await getAbstractReceiptData({ sub_domain: subDomain });
      const filtered = activeSeriesList.length === 0
        ? (data || [])
        : (data || []).filter((record) => activeSeriesList.includes(record.series));
      setAbstractReceiptList(filtered);
    } catch (error) {
      console.error('Failed to fetch abstract receipt list:', error);
    }
  }, [activeSeriesList]);

  const handleUseExistingChoice = (choice) => {
    setUseExisting(choice);
    setSelectedAbstractId('');
    if (choice && abstractReceiptList.length === 0) {
      fetchAbstractReceiptList();
    }
    if (!choice) {
      setForm(prev => ({
        ...prev,
        speaker_name: '',
        email: '',
        organization: '',
        abstract_title: '',
        country: '',
      }));
    }
  };

  const handleAbstractReceiptSelect = (e) => {
    const id = e.target.value;
    setSelectedAbstractId(id);
    if (!id) return;

    const record = abstractReceiptList.find(r => String(r.id) === id);
    if (!record) return;

    setForm(prev => ({
      ...prev,
      event_type: record.event_type || '',
      series: record.series || '',
      venue: record.venue || '',
      country: record.country || '',
      speaker_name: record.speaker_name || '',
      email: record.email || '',
      organization: record.organization || '',
      abstract_title: combineAbstractTitles(record),
      event_title: record.event_title || '',
      event_date: record.event_date || '',
    }));
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
      'event_type',
      'invitation_category',
      'country',
      'speaker_name',
      'email',
      'series',
      'venue',
    ];

    requiredFields.forEach((field) => {
      if (!form[field]?.trim()) {
        newErrors[field] = 'Please fill out this field';
      }
    });

    if (isSpeakerCategory(form.invitation_category) && !form.abstract_type?.trim()) {
      newErrors.abstract_type = 'Please select an abstract type';
    }

    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'Enter a valid email';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const backToList = () => {
    setView('list');
    setFormMode('');
    setError(null);
    setErrors({});
    setForm(emptyForm);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsLoading(true);
    setError('');

    // Reuses the cached record from mount instead of calling
    // getprogramManager() again — removes one full round-trip from Save/Update.
    const pm = programManagerData || {};

    try {
      const submissionData = {
        ...form,
        conference_committee: pm.conference_committee || "",
        conf_shortname: pm.conf_shortname || "",
        expertise: pm.expertise || "",
        coordinator_name: pm.coordinator_name || "",
        designation: pm.designation || "",
        config_email: pm.config_email || "",
        abstract_title: isNoAbstractCategory(form.invitation_category) ? '' : form.abstract_title,
        abstract_type: form.abstract_type || 'N/A',
      };

      let response;
      if (formMode === 'add') {
        response = await addData(submissionData);

        if (!response?.data?.id) {
          throw new Error('Failed to get ID for new record');
        }

        const data = await getDataById({ id: response.data.id });
        navigate('/invitationLetterPdf', { state: { data } });

      } else if (formMode === 'edit') {
        if (!form.id) {
          throw new Error('Record ID is required for editing');
        }

        await updateData(submissionData);
        const data = await getDataById({ id: form.id });
        navigate('/invitationLetterPdf', { state: { data } });
      }

    } catch (error) {
      console.error('Error during form submission:', error);
      setError(error.message || 'Failed to submit form. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const openForm = async (mode, row = null) => {
    setFormMode(mode);
    setError(null);
    setErrors({});
    setUseExisting(null);
    setSelectedAbstractId('');

    if (mode === 'add') {
      if (!pmLoaded && pmFetchPromiseRef.current) {
        await pmFetchPromiseRef.current;
      }
      if (!programManagerData) {
        Swal.fire({
          icon: 'warning',
          title: 'Program Manager Not Found',
          text: 'No Program Manager details found. Please update the Program Manager details.',
          showCancelButton: true,
          confirmButtonText: 'Update Now',
          cancelButtonText: 'Cancel',
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33'
        }).then((result) => {
          if (result.isConfirmed) {
            window.location.href = `/programManager`;
          }
        });
        return;
      }

      const status = programManagerData?.status ?? true;
      if (status === false) {
        Swal.fire({
          icon: 'warning',
          title: 'Program Manager Inactive',
          text: 'Your Program Manager details are inactive. Please contact the admin.',
          confirmButtonColor: '#3085d6'
        });
        return;
      }

      try {
        setForm({ ...emptyForm });

        const result = await getReceiptId({ sub_domain: subDomain, type: 'invitation' });
        if (result?.invitation_id) {
          setForm(prev => ({ ...prev, invitation_id: result.invitation_id }));
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    }

    if (mode === 'edit' && row) {
      setForm({ ...emptyForm, ...row });
      setUseExisting(false);
    }

    setView('form');
  };

  const handleSearchChange = (e) => setSearchText(e.target.value);
  const handlePageChange = (page) => setCurrentPage(page);

  const filteredData = useMemo(() => {
    const startDateObj = startDate ? new Date(startDate) : null;
    const endDateObj = endDate ? new Date(endDate) : null;

    return dataList
      .filter((data) => {
        const matchesText =
          data.speaker_name?.toLowerCase().includes(searchText.toLowerCase()) ||
          data.invitation_id?.toLowerCase().includes(searchText.toLowerCase()) ||
          data.country?.toLowerCase().includes(searchText.toLowerCase());

        const date = data.createdAt ? new Date(data.createdAt) : null;
        const inRange = (startDateObj && endDateObj)
          ? date >= startDateObj && date <= endDateObj
          : true;

        const matchCountry = !countryFilter || data.country === countryFilter;

        return matchesText && inRange && matchCountry;
      }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [dataList, searchText, startDate, endDate, countryFilter]);

  const columns = [
    {
      name: '#',
      selector: (row, index) => (currentPage - 1) * 10 + (index + 1),
      sortable: false,
      width: '60px',
    },
    { name: 'Invitation ID', selector: (row) => row.invitation_id, sortable: true, wrap: true },
    { name: 'Speaker / Delegate Name', selector: (row) => row.speaker_name, sortable: true, wrap: true },
    { name: 'Category', selector: (row) => row.invitation_category, sortable: true, wrap: true },
    { name: 'Abstract Type', selector: (row) => row.abstract_type, sortable: true, wrap: true },
    { name: 'Country', selector: (row) => getCountryName(row.country), sortable: true, wrap: true },
    {
      name: 'Actions',
      cell: (row) => (
        <div>
          <button className="btn btn-warning btn-sm me-2" onClick={() => openForm('edit', row)}>
            Edit
          </button>
        </div>
      ),
    },
  ];

  const renderList = () => (
    <div>
      <div className=" row m-3 d-flex flex-column gap-3 ">
        <div className="d-flex gap-2">
          <div className="col-md-2">
            <span className='fs-5 text-dark'>Countries</span>
            <select
              className="form-control"
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
            >
              <option value="">All Countries</option>
              {dropdownData.countries.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-2">
            <span className='fs-5 text-dark'>Start Date</span>
            <input
              type="date"
              className="form-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="col-md-2">
            <span className='fs-5 text-dark'>End Date</span>
            <input
              type="date"
              className="form-control"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="container-fluid p-4">
        <div className="row">
          <div className="col-8">
            <p className='fs-4 text-dark'>Letter Generation / Invitation Letter</p>
          </div>
          <div className="col-4 text-end">
            <button className="btn btn-primary mb-3" onClick={() => openForm('add')}>
              Add
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger">
            {error}
          </div>
        )}

        <DataTable
          columns={columns}
          data={filteredData}
          pagination
          highlightOnHover
          responsive
          striped
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 20, 50, 100, 200, 500]}
          onChangePage={handlePageChange}
          subHeader
          subHeaderComponent={
            <div>
              <input
                className="form-control"
                type="text"
                placeholder="Search"
                value={searchText}
                onChange={handleSearchChange}
              />
            </div>
          }
        />
      </div>
    </div>
  );

  const canHaveAbstract = !isNoAbstractCategory(form.invitation_category);
  const abstractTypeOptions = getAbstractTypeOptions(form.invitation_category, form.event_type);

  const renderForm = () => (
    <div className="container-fluid p-4">
      <div className="row mb-3">
        <div className="col-8">
          <p className='fs-4 text-dark'>{formMode === 'add' ? 'Add Invitation Letter' : 'Edit Invitation Letter'}</p>
        </div>
        <div className="col-4 text-end">
          <button className="btn btn-outline-secondary" onClick={backToList} disabled={isLoading}>
            Back to List
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      <Form>
        <div className='row'>
          <Form.Group className="col-md-3 mb-3">
            <Form.Label className='fs-5 text-dark'>Invitation ID<small className="text-danger fs-5"> * </small></Form.Label>
            <Form.Control type="text" name="invitation_id" value={form.invitation_id} readOnly />
          </Form.Group>

          {formMode === 'add' && (
            <Form.Group className="col-md-9 mb-3">
              <Form.Label className='fs-5 text-dark d-block'>Generate from an existing Abstract Receipt?</Form.Label>
              <Form.Check
                inline
                type="radio"
                id="use-existing-yes"
                label="Yes"
                checked={useExisting === true}
                onChange={() => handleUseExistingChoice(true)}
              />
              <Form.Check
                inline
                type="radio"
                id="use-existing-no"
                label="No"
                checked={useExisting === false}
                onChange={() => handleUseExistingChoice(false)}
              />
            </Form.Group>
          )}

          {useExisting === true && (
            <Form.Group className="col-md-12 mb-3">
              <Form.Label className='fs-5 text-dark'>Select Abstract Receipt</Form.Label>
              <Form.Select value={selectedAbstractId} onChange={handleAbstractReceiptSelect}>
                <option value="">Select</option>
                {abstractReceiptList.map((record) => (
                  <option key={record.id} value={record.id}>
                    {record.speaker_name} - {record.abstract_id}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          <Form.Group className="col-md-4 mb-3">
            <Form.Label className='fs-5 text-dark'>
              Invitation Category<small className="text-danger fs-5"> * </small>
            </Form.Label>
            <Form.Select
              name="invitation_category"
              value={form.invitation_category}
              onChange={handleChange}
              isInvalid={!!errors.invitation_category}
            >
              <option value="">Select</option>
              {dropdownData.invitationType.map((item) => (
                <option key={item.id} value={item.category}>
                  {item.category}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.invitation_category}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="col-md-4 mb-3">
            <Form.Label className='fs-5 text-dark'>Event Type<small className="text-danger fs-5"> *</small></Form.Label>
            <Form.Select name="event_type" value={form.event_type} onChange={handleChange} isInvalid={!!errors.event_type}>
              <option value="">Select</option>
              {dropdownData.eventType.map((eventType) => (
                <option key={eventType.type} value={eventType.type}>
                  {eventType.type}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">  {errors.event_type} </Form.Control.Feedback>
          </Form.Group>

          {isKeynoteCategory(form.invitation_category) && (
            <Form.Group className="col-md-4 mb-3">
              <Form.Label className='fs-5 text-dark'>Abstract Type</Form.Label>
              <Form.Control type="text" value={form.abstract_type} readOnly />
            </Form.Group>
          )}

          {isSpeakerCategory(form.invitation_category) && (
            <Form.Group className="col-md-4 mb-3">
              <Form.Label className='fs-5 text-dark'>Abstract Type<small className="text-danger fs-5"> * </small></Form.Label>
              <Form.Select
                name="abstract_type"
                value={form.abstract_type}
                onChange={handleChange}
                isInvalid={!!errors.abstract_type}
                disabled={!form.event_type}
              >
                <option value="">{form.event_type ? 'Select' : 'Select Event Type first'}</option>
                {abstractTypeOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.abstract_type}
              </Form.Control.Feedback>
            </Form.Group>
          )}

          <Form.Group className="col-md-4 mb-3">
            <Form.Label className='fs-5 text-dark'>Series</Form.Label>
            <Form.Control type="text" name="series" value={form.series} readOnly />
          </Form.Group>
          <Form.Group className="col-md-4 mb-3">
            <Form.Label className='fs-5 text-dark'>Venue</Form.Label>
            <Form.Control type="text" name="venue" value={form.venue} readOnly />
          </Form.Group>

          <Form.Group className="col-md-4 mb-3">
            <Form.Label className='fs-5 text-dark'>Country<small className="text-danger fs-5"> * </small></Form.Label>
            <Form.Select name="country" value={form.country} onChange={handleChange} isInvalid={!!errors.country}>
              <option value="">Select</option>
              {dropdownData.countries.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">  {errors.country} </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="col-md-4 mb-3">
            <Form.Label className='fs-5 text-dark'>Speaker / Delegate Name<small className="text-danger fs-5"> * </small></Form.Label>
            <Form.Control type="text" name="speaker_name" value={form.speaker_name} onChange={handleChange} isInvalid={!!errors.speaker_name} />
            <Form.Control.Feedback type="invalid">  {errors.speaker_name} </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="col-md-4 mb-3">
            <Form.Label className='fs-5 text-dark'>Email<small className="text-danger fs-5"> * </small></Form.Label>
            <Form.Control type="email" name="email" value={form.email} onChange={handleChange} isInvalid={!!errors.email} />
            <Form.Control.Feedback type="invalid">  {errors.email} </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="col-md-6 mb-3">
            <Form.Label className='fs-5 text-dark'>Affiliation</Form.Label>
            <Form.Control type="text" name="organization" value={form.organization} onChange={handleChange} />
          </Form.Group>

          {canHaveAbstract && (
            <Form.Group className="col-md-6 mb-3">
              <Form.Label className='fs-5 text-dark'>Presentation Title</Form.Label>
              <Form.Control type="text" name="abstract_title" value={form.abstract_title} onChange={handleChange} />
            </Form.Group>
          )}
        </div>

        <div className="d-flex justify-content-end gap-2 mt-3">
          <button type="button" className="btn btn-secondary" onClick={backToList} disabled={isLoading}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={isLoading}>
            {isLoading && (
              <BsSpinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
            )}
            {isLoading ? 'Processing...' : formMode === 'add' ? 'Save' : 'Update'}
          </button>
        </div>
      </Form>
    </div>
  );

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Header />
        {loading ? (
          <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: "300px" }}>
            <Spinner />
          </div>
        ) : (
          <div>
            {view === 'list' ? renderList() : renderForm()}
            <Footer />
          </div>
        )}
      </div>
    </div>
  );
};

export default InvitationLetter;